import { wishlist, WISHLIST_CHANGED } from '@theme/wishlist';

/**
 * <wishlist-page> — renders the saved products as a grid on /pages/wishlist.
 *
 * Because the list of saved IDs lives client-side, each card is fetched with the
 * Section Rendering API (same technique as assets/variant-picker.js):
 *   GET {Shopify.routes.root}products/{handle}?section_id={data-card-section}
 * and the `.mh-collection-card` node is lifted out of the response so the card is
 * byte-for-byte the one used on collection pages.
 */
class WishlistPage extends HTMLElement {
  #grid;
  #empty;
  #status;
  #root;
  #section;
  #loaded = false;

  connectedCallback() {
    this.#grid = this.querySelector('[data-wishlist-grid]');
    this.#empty = this.querySelector('[data-wishlist-empty]');
    this.#status = this.querySelector('[data-wishlist-status]');
    this.#section = this.dataset.cardSection || 'wishlist-card';

    const root = window.Shopify?.routes?.root || '/';
    this.#root = root.endsWith('/') ? root : `${root}/`;

    document.addEventListener(WISHLIST_CHANGED, this.#onChange);
    this.#renderAll();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_CHANGED, this.#onChange);
  }

  #onChange = (event) => {
    const detail = event.detail || {};
    if (detail.action === 'remove' && detail.id) return this.#removeItem(detail.id);
    if (detail.action === 'add' && detail.id && this.#loaded) return this.#addItem(detail.id);
    if (detail.action === 'sync' || detail.action === 'init') return this.#renderAll();
  };

  async #renderAll() {
    const items = wishlist.getAll();
    this.#grid.replaceChildren();
    this.#toggleEmpty(items.length === 0);

    if (!items.length) {
      this.#loaded = true;
      return;
    }

    if (this.#status) this.#status.textContent = this.dataset.loadingText || '';

    const results = await Promise.allSettled(items.map((item) => this.#fetchCard(item)));

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        this.#grid.appendChild(this.#li(items[index].id, result.value));
      } else if (result.status === 'rejected' && result.reason === 'not-found') {
        wishlist.remove(items[index].id);
      }
    });

    if (this.#status) this.#status.textContent = '';
    this.#toggleEmpty(this.#grid.children.length === 0);
    this.#loaded = true;
  }

  async #fetchCard(item) {
    if (!item.handle) throw 'not-found';
    const url = new URL(`${this.#root}products/${item.handle}`, window.location.origin);
    url.searchParams.set('section_id', this.#section);

    const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
    if (response.status === 404) throw 'not-found';
    if (!response.ok) throw new Error(String(response.status));

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return (
      doc.querySelector('.mh-collection-card') ||
      doc.querySelector('.shopify-section')?.firstElementChild ||
      null
    );
  }

  #li(id, node) {
    const li = document.createElement('li');
    li.className = 'mh-wishlist-page__item';
    li.dataset.productId = String(id);
    li.appendChild(node);
    return li;
  }

  async #addItem(id) {
    const item = wishlist.getAll().find((entry) => entry.id === String(id));
    if (!item || this.#grid.querySelector(`.mh-wishlist-page__item[data-product-id="${id}"]`)) return;
    try {
      const node = await this.#fetchCard(item);
      if (node) {
        this.#grid.prepend(this.#li(id, node));
        this.#toggleEmpty(false);
      }
    } catch (_) {
      // Non-fatal — the card just won't appear until reload.
    }
  }

  #removeItem(id) {
    this.#grid.querySelector(`.mh-wishlist-page__item[data-product-id="${id}"]`)?.remove();
    this.#toggleEmpty(this.#grid.children.length === 0);
  }

  #toggleEmpty(isEmpty) {
    if (this.#empty) this.#empty.hidden = !isEmpty;
    this.#grid.hidden = isEmpty;
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
